/**
 * Superadmin — Machines Configuration
 * Per user spec: "machine configuration machines etc"
 * - Templates: pré-configurations réutilisables (par ex. "Siemens Atellica — Service standard")
 * - Policies: règles de sécurité (auth, encryption, retention)
 * - Calibration: schedules, derniers calibrages, prochaine échéance
 * - Auto-test: scénarios de validation automatique (ping, ASTM, HL7, throughput)
 * - Mapping: routing rules (which device → which department, room, patient cohort)
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Server, Plus, Edit, Trash2, Save, Settings, Wrench, Activity, Shield, MapPin, Calendar, CheckCircle2, XCircle, FileText, Box, Zap, Network, AlertCircle, Clock, Hash,
} from "lucide-react";
import { getMockProfiles, STATUS_TONE, STATUS_LABEL, type DeviceProfile } from "@/lib/device/profiles";

export const Route = createFileRoute("/admin/machines")({
  component: MachinesConfig,
});

interface MachineTemplate {
  id: string;
  name: string;
  vendor: string;
  model: string;
  transport: string;
  driver: string;
  description: string;
  defaultHost: string;
  defaultPort: number;
  appliesTo: string[];      // departments
  safetyClass: "A" | "B" | "C";
  policies: {
    requireTLS: boolean;
    requireMutualTLS: boolean;
    requireIpWhitelist: boolean;
    retentionDays: number;
    pseudonymizeInTransit: boolean;
    auditEveryMessage: boolean;
  };
  calibration: {
    intervalDays: number;
    lastCalibration: string;
    nextCalibration: string;
    calibrationProcedure: string;
  };
  autoTest: {
    enabled: boolean;
    interval: "hourly" | "daily" | "weekly";
    scenarios: string[];     // ["ping", "astm-handshake", "hl7-ack", "throughput"]
    alertIfFail: boolean;
  };
  installed: number;         // number of instances
}

const TEMPLATES: MachineTemplate[] = [
  {
    id: "tpl-roche-cobas",
    name: "Roche Cobas — Laboratoire central",
    vendor: "Roche",
    model: "Cobas e411",
    transport: "ASTM E1394",
    driver: "@dikdoc/driver-roche-cobas@2.3.1",
    description: "Analyseur immuno-sérologique pour dosage hormonal, marqueurs tumoraux, vitamines",
    defaultHost: "10.42.1.10",
    defaultPort: 2575,
    appliesTo: ["LAB", "HEMA"],
    safetyClass: "B",
    policies: {
      requireTLS: true,
      requireMutualTLS: true,
      requireIpWhitelist: true,
      retentionDays: 730,           // 2 ans
      pseudonymizeInTransit: true,
      auditEveryMessage: true,
    },
    calibration: {
      intervalDays: 30,
      lastCalibration: "2026-05-15",
      nextCalibration: "2026-06-14",
      calibrationProcedure: "Calibration 2-point + contrôle qualité quotidien (3 niveaux)",
    },
    autoTest: {
      enabled: true,
      interval: "daily",
      scenarios: ["ping", "astm-handshake", "throughput"],
      alertIfFail: true,
    },
    installed: 4,
  },
  {
    id: "tpl-philips-mx800",
    name: "Philips IntelliVue — Réanimation",
    vendor: "Philips",
    model: "IntelliVue MX800",
    transport: "IEEE 11073",
    driver: "@dikdoc/driver-philips-intellivue@2.1.4",
    description: "Moniteur patient multi-paramétrique (ECG, SpO2, NIBP, T°, FR) avec alarmes",
    defaultHost: "10.42.5.20",
    defaultPort: 11112,
    appliesTo: ["ICU", "URG", "CHIR"],
    safetyClass: "C",
    policies: {
      requireTLS: true,
      requireMutualTLS: true,
      requireIpWhitelist: true,
      retentionDays: 3650,          // 10 ans (données critiques)
      pseudonymizeInTransit: false, // garder identification pour urgence vitale
      auditEveryMessage: true,
    },
    calibration: {
      intervalDays: 180,
      lastCalibration: "2026-03-10",
      nextCalibration: "2026-09-06",
      calibrationProcedure: "Vérification annuelle constructeur + tests biomédicaux mensuels",
    },
    autoTest: {
      enabled: true,
      interval: "hourly",
      scenarios: ["ping", "hl7-ack"],
      alertIfFail: true,
    },
    installed: 8,
  },
  {
    id: "tpl-fujifilm-fcr",
    name: "Fujifilm FCR — Imagerie",
    vendor: "Fujifilm",
    model: "FCR Carbon XL",
    transport: "DICOM",
    driver: "@dikdoc/driver-fujifilm-fcr@2.0.5",
    description: "Computed Radiography pour radio standard (thorax, abdomen, os)",
    defaultHost: "10.42.7.30",
    defaultPort: 104,
    appliesTo: ["IMAG", "URG", "ORTH"],
    safetyClass: "B",
    policies: {
      requireTLS: true,
      requireMutualTLS: true,
      requireIpWhitelist: true,
      retentionDays: 3650,          // 10 ans
      pseudonymizeInTransit: true,
      auditEveryMessage: false,     // high volume → échantillonnage
    },
    calibration: {
      intervalDays: 90,
      lastCalibration: "2026-04-22",
      nextCalibration: "2026-07-21",
      calibrationProcedure: "Calibration qualité image (SNR, MTF) + contrôle hebdomadaire",
    },
    autoTest: {
      enabled: true,
      interval: "daily",
      scenarios: ["ping", "throughput"],
      alertIfFail: true,
    },
    installed: 2,
  },
  {
    id: "tpl-medtronic-cgm",
    name: "Medtronic Guardian — CGM",
    vendor: "Medtronic",
    model: "Guardian Connect",
    transport: "Propriétaire HTTPS",
    driver: "@dikdoc/driver-medtronic-guardian@1.2.3",
    description: "Continuous Glucose Monitoring pour patients diabétiques ambulatoires",
    defaultHost: "api.medtronic-diabetes.com",
    defaultPort: 443,
    appliesTo: ["ENDO", "DIAB", "MED"],
    safetyClass: "B",
    policies: {
      requireTLS: true,
      requireMutualTLS: false,
      requireIpWhitelist: false,
      retentionDays: 1825,          // 5 ans
      pseudonymizeInTransit: true,
      auditEveryMessage: true,
    },
    calibration: {
      intervalDays: 365,
      lastCalibration: "2026-02-01",
      nextCalibration: "2027-02-01",
      calibrationProcedure: "Ré-étalonnage annuel par bandelette glycémique",
    },
    autoTest: {
      enabled: true,
      interval: "hourly",
      scenarios: ["ping"],
      alertIfFail: true,
    },
    installed: 12,
  },
];

function MachinesConfig() {
  const [templates, setTemplates] = useState<MachineTemplate[]>(TEMPLATES);
  const [selected, setSelected] = useState<MachineTemplate | null>(null);
  const [editing, setEditing] = useState<MachineTemplate | null>(null);
  const [adding, setAdding] = useState(false);

  const allDevices = getMockProfiles();

  const stats = useMemo(() => {
    const totalInstalled = templates.reduce((s, t) => s + t.installed, 0);
    const nextCalibration = templates
      .map((t) => ({ tpl: t, date: new Date(t.calibration.nextCalibration) }))
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, 3);
    const overdue = templates.filter((t) => new Date(t.calibration.nextCalibration) < new Date()).length;
    return { totalInstalled, nextCalibration, overdue };
  }, [templates]);

  function daysUntil(iso: string): number {
    return Math.floor((new Date(iso).getTime() - Date.now()) / (24 * 3600 * 1000));
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2">
        <Badge variant="destructive" className="text-[10px]">SUPERADMIN</Badge>
        <span className="text-sm text-muted-foreground">· Templates, calibration, auto-test, policies, mapping</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Server className="h-5 w-5" /> Configuration machines
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {templates.length} templates · {stats.totalInstalled} machines déployées · {stats.overdue} calibration en retard
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Templates</div>
            <div className="mt-1 text-2xl font-bold">{templates.length}</div>
            <div className="text-xs text-muted-foreground">configurations réutilisables</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Instances déployées</div>
            <div className="mt-1 text-2xl font-bold text-emerald-400">{stats.totalInstalled}</div>
            <div className="text-xs text-muted-foreground">machines actives</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Calibrations en retard</div>
            <div className={`mt-1 text-2xl font-bold ${stats.overdue > 0 ? "text-red-400" : "text-emerald-400"}`}>{stats.overdue}</div>
            <div className="text-xs text-muted-foreground">à planifier</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Prochaines calibrations</div>
            <div className="mt-2 text-xs space-y-1">
              {stats.nextCalibration.map((c) => (
                <div key={c.tpl.id} className="flex items-center gap-1">
                  <span className="font-mono text-[10px]">{c.date.toISOString().slice(0, 10)}</span>
                  <span className="text-muted-foreground truncate">({c.tpl.vendor})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates"><Box className="h-3.5 w-3.5 mr-1" /> Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="instances"><Server className="h-3.5 w-3.5 mr-1" /> Instances ({allDevices.length})</TabsTrigger>
          <TabsTrigger value="calibration"><Wrench className="h-3.5 w-3.5 mr-1" /> Calibration</TabsTrigger>
          <TabsTrigger value="autotest"><Activity className="h-3.5 w-3.5 mr-1" /> Auto-test</TabsTrigger>
          <TabsTrigger value="policies"><Shield className="h-3.5 w-3.5 mr-1" /> Policies</TabsTrigger>
        </TabsList>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Chaque template encapsule : driver · policies · calibration · auto-test · mapping
            </p>
            <Button size="sm" onClick={() => setAdding(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Nouveau template</Button>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {templates.map((t) => (
              <Card key={t.id} className="cursor-pointer hover:border-primary/30" onClick={() => setSelected(t)}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-md bg-primary/10 text-primary">
                      <Server className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium truncate">{t.name}</span>
                        <Badge variant="outline" className="text-[10px]">Classe {t.safetyClass}</Badge>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{t.description}</div>
                      <div className="mt-1 text-[10px] text-muted-foreground font-mono">
                        {t.driver} · {t.defaultHost}:{t.defaultPort}
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-[10px]">
                        <Badge variant="secondary" className="h-4 text-[9px]">{t.installed} installés</Badge>
                        {t.appliesTo.map((d) => <Badge key={d} variant="outline" className="h-4 text-[9px]">{d}</Badge>)}
                        {t.policies.requireMutualTLS && <Badge variant="secondary" className="h-4 text-[9px]">mTLS</Badge>}
                        {t.autoTest.enabled && <Badge variant="default" className="h-4 text-[9px]">auto-test {t.autoTest.interval}</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditing(t); }}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* INSTANCES TAB */}
        <TabsContent value="instances" className="space-y-1.5">
          <p className="text-sm text-muted-foreground">
            Toutes les machines physiques connectées · Héritent des policies du template assigné
          </p>
          {allDevices.map((d) => {
            const tpl = templates.find((t) => t.vendor === d.vendor && t.model === d.model);
            return (
              <Card key={d.id}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <Server className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">{d.vendor} {d.model}</span>
                        <Badge variant={STATUS_TONE[d.status]} className="text-[10px]">{STATUS_LABEL[d.status]}</Badge>
                        {tpl && <Badge variant="outline" className="text-[10px]">Template: {tpl.id}</Badge>}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {d.host}:{d.port} · FW {d.firmware} · {d.iec11073Conformant ? "IEEE 11073" : d.transport}
                      </div>
                    </div>
                    <div className="text-right text-[10px]">
                      <div className="text-muted-foreground">Prochaine calibration</div>
                      {tpl ? (
                        <div className={`font-mono ${daysUntil(tpl.calibration.nextCalibration) < 7 ? "text-red-400" : "text-emerald-400"}`}>
                          {tpl.calibration.nextCalibration} ({daysUntil(tpl.calibration.nextCalibration)}j)
                        </div>
                      ) : <div className="text-amber-400">non assigné</div>}
                    </div>
                    <Button size="sm" variant="outline"><Settings className="h-3 w-3" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* CALIBRATION TAB */}
        <TabsContent value="calibration" className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Calendrier de calibration</CardTitle>
              <CardDescription>Suivi de tous les étalonnages programmés · IEC 62304 classe B/C</CardDescription>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-muted-foreground">
                    <th className="text-left p-2 text-xs uppercase">Template</th>
                    <th className="text-left p-2 text-xs uppercase">Classe</th>
                    <th className="text-left p-2 text-xs uppercase">Dernière</th>
                    <th className="text-left p-2 text-xs uppercase">Prochaine</th>
                    <th className="text-left p-2 text-xs uppercase">Intervalle</th>
                    <th className="text-left p-2 text-xs uppercase">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => {
                    const days = daysUntil(t.calibration.nextCalibration);
                    const isOverdue = days < 0;
                    const isSoon = days >= 0 && days < 7;
                    return (
                      <tr key={t.id} className="border-b border-white/5">
                        <td className="p-2 font-medium text-sm">{t.vendor} {t.model}</td>
                        <td className="p-2"><Badge variant="outline" className="text-[10px]">Classe {t.safetyClass}</Badge></td>
                        <td className="p-2 font-mono text-xs">{t.calibration.lastCalibration}</td>
                        <td className="p-2 font-mono text-xs">{t.calibration.nextCalibration}</td>
                        <td className="p-2 text-xs">{t.calibration.intervalDays}j</td>
                        <td className="p-2">
                          {isOverdue ? <Badge variant="destructive" className="text-[10px]">⚠ Retard ({Math.abs(days)}j)</Badge> :
                            isSoon ? <Badge variant="secondary" className="text-[10px]">Imminent ({days}j)</Badge> :
                              <Badge variant="outline" className="text-[10px] text-emerald-400">OK ({days}j)</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AUTOTEST TAB */}
        <TabsContent value="autotest" className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Scénarios d'auto-test</CardTitle>
              <CardDescription>Tests automatiques exécutés selon l'intervalle configuré · Alerte en cas d'échec</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center gap-3 rounded-md border border-white/5 bg-white/[0.02] p-3">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{t.vendor} {t.model}</div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      Intervalle: <span className="font-mono">{t.autoTest.interval}</span> · Scénarios:
                      {t.autoTest.scenarios.map((s) => <code key={s} className="ml-1 text-[10px] bg-white/5 px-1 rounded">{s}</code>)}
                    </div>
                  </div>
                  {t.autoTest.enabled ? (
                    <Badge variant="default" className="text-[10px]"><CheckCircle2 className="h-3 w-3 mr-0.5" /> ON</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">OFF</Badge>
                  )}
                  <Button size="sm" variant="outline"><Zap className="h-3 w-3 mr-1" /> Lancer maintenant</Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* POLICIES TAB */}
        <TabsContent value="policies" className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Policies de sécurité par template</CardTitle>
              <CardDescription>Authentification, chiffrement, rétention, audit</CardDescription>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-muted-foreground">
                    <th className="text-left p-2 text-xs uppercase">Template</th>
                    <th className="text-center p-2 text-xs uppercase">TLS</th>
                    <th className="text-center p-2 text-xs uppercase">mTLS</th>
                    <th className="text-center p-2 text-xs uppercase">IP Whitelist</th>
                    <th className="text-right p-2 text-xs uppercase">Rétention</th>
                    <th className="text-center p-2 text-xs uppercase">Audit</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} className="border-b border-white/5">
                      <td className="p-2 font-medium text-sm">{t.vendor} {t.model}</td>
                      <td className="text-center p-2">{t.policies.requireTLS ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 inline" /> : <XCircle className="h-3.5 w-3.5 text-red-400 inline" />}</td>
                      <td className="text-center p-2">{t.policies.requireMutualTLS ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 inline" /> : <XCircle className="h-3.5 w-3.5 text-red-400 inline" />}</td>
                      <td className="text-center p-2">{t.policies.requireIpWhitelist ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 inline" /> : <XCircle className="h-3.5 w-3.5 text-red-400 inline" />}</td>
                      <td className="text-right p-2 font-mono text-xs">{t.policies.retentionDays}j</td>
                      <td className="text-center p-2">{t.policies.auditEveryMessage ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 inline" /> : <Badge variant="outline" className="text-[9px]">échant.</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={!!editing || adding} onOpenChange={(o) => { if (!o) { setEditing(null); setAdding(false); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="h-4 w-4" /> {adding ? "Nouveau template machine" : "Modifier le template"}
            </DialogTitle>
            <DialogDescription>
              Modifications journalisées dans l'audit WORM · Audit signé par superadmin
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nom du template" value={editing.name} />
                <Field label="Driver" value={editing.driver} />
                <Field label="Vendor" value={editing.vendor} />
                <Field label="Model" value={editing.model} />
                <Field label="Host par défaut" value={editing.defaultHost} />
                <Field label="Port" value={String(editing.defaultPort)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Description</label>
                <Textarea defaultValue={editing.description} className="mt-1" />
              </div>
              <div className="border-t pt-3">
                <div className="text-sm font-medium mb-2">Policies</div>
                <div className="space-y-2">
                  <CheckboxRow label="TLS obligatoire" checked={editing.policies.requireTLS} />
                  <CheckboxRow label="Mutual TLS (mTLS)" checked={editing.policies.requireMutualTLS} />
                  <CheckboxRow label="IP Whitelist" checked={editing.policies.requireIpWhitelist} />
                  <CheckboxRow label="Pseudonymisation en transit" checked={editing.policies.pseudonymizeInTransit} />
                  <CheckboxRow label="Audit de chaque message" checked={editing.policies.auditEveryMessage} />
                </div>
              </div>
              <div className="border-t pt-3">
                <div className="text-sm font-medium mb-2">Calibration</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Intervalle (jours)" value={String(editing.calibration.intervalDays)} />
                  <Field label="Prochaine" value={editing.calibration.nextCalibration} />
                </div>
              </div>
              <div className="border-t pt-3">
                <div className="text-sm font-medium mb-2">Auto-test</div>
                <div className="space-y-2">
                  <CheckboxRow label="Activé" checked={editing.autoTest.enabled} />
                  <div>
                    <label className="text-xs text-muted-foreground">Scénarios</label>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      {["ping", "astm-handshake", "hl7-ack", "throughput"].map((s) => (
                        <Badge key={s} variant={editing.autoTest.scenarios.includes(s) ? "default" : "outline"} className="text-[10px]">{s}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setAdding(false); }}>Annuler</Button>
            <Button><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground uppercase">{label}</label>
      <Input defaultValue={value} className="mt-1" />
    </div>
  );
}

function CheckboxRow({ label, checked }: { label: string; checked: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox checked={checked} />
      <span className="text-sm">{label}</span>
    </div>
  );
}
